import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { getBillingStatus } from "@/lib/billingApi";

// Product gate: `guard()` = authenticated (FREE tools: Montage, Music); `guard(true)` = authenticated AND
// ACTIVE subscription (paid AI flows, billing = source of truth, cached per user). The backend enforces the same rules.
let cache: { userId: string; active: boolean; at: number } | null = null;
const CACHE_MS = 60_000;

export const resetAccessCache = () => { cache = null; };

export async function hasActiveSubscription(userId: string): Promise<boolean> {
  if (cache && cache.userId === userId && Date.now() - cache.at < CACHE_MS) return cache.active;
  try {
    const st = await getBillingStatus();
    cache = { userId, active: !!st.is_active, at: Date.now() };
    return cache.active;
  } catch {
    return false; // fail closed
  }
}

export function useAccessGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const guard = useCallback(async (requireSubscription = false): Promise<boolean> => {
    if (!user) {
      toast.info(t("access.loginRequired"));
      navigate("/login", { state: { from: location.pathname } });
      return false;
    }
    if (requireSubscription && !(await hasActiveSubscription(user.id))) {
      toast.info(t("access.subscriptionRequired"), { action: { label: t("billing.subscribe"), onClick: () => navigate("/billing#plans") } });
      navigate("/billing#plans");
      return false;
    }
    return true;
  }, [user, navigate, location.pathname, t]);

  return { guard, isAuthenticated: !!user };
}
